import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Loader2,
  Download,
  RefreshCw,
  FileText,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../lib/supabase';
import { useTimezone } from '../contexts/TimezoneContext';
import { saveBlobWithPicker, openManualDownload } from '../lib/fileSave';
import { presentedAIJobFilename } from '../lib/reportFilenames';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RecentAIJobsPage — workspace view for every AI-generated artefact the
 * user has triggered. Used as a fallback when the toast from the original
 * "Generate report" click was missed (e.g. tab closed mid-render) and as
 * a way to re-download a report within the 7-day server-side TTL without
 * re-running the GPT-4o job from scratch.
 *
 * Backend contract (routes/ai_jobs.py):
 *   GET /api/ai-jobs?kind=&status=&limit=&offset= → { jobs: [...], pagination }
 *   Each job carries:
 *     { id, kind, status, audit_id, filename, file_size, mime_type,
 *       error, created_at, completed_at, file_available }
 *   ``file_available=false`` means the upload sweeper has reclaimed the
 *   artefact (>7 days old) — we surface this as an "Expired" badge with
 *   a hint to re-generate from the audit page.
 */

// Friendly labels for the ``kind`` field. The backend is intentionally
// permissive (any string is accepted) so unknown kinds fall back to a
// title-cased rendering of the raw value.
const KIND_LABELS = {
  remediation_report: 'Full remediation report',
  category_report: 'Category PDF report',
};

const STATUS_CONFIG = {
  complete: {
    icon: CheckCircle2,
    color: 'text-[hsl(var(--success))]',
    bg: 'bg-[hsl(var(--success))]/10',
    label: 'Ready',
  },
  running: {
    icon: Loader2,
    color: 'text-accent',
    bg: 'bg-accent/10',
    label: 'Generating',
    animate: true,
  },
  pending: {
    icon: Clock,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    label: 'Queued',
  },
  failed: {
    icon: XCircle,
    color: 'text-[hsl(var(--destructive))]',
    bg: 'bg-[hsl(var(--destructive))]/10',
    label: 'Failed',
  },
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'complete', label: 'Ready to download' },
  { value: 'running', label: 'Generating' },
  { value: 'pending', label: 'Queued' },
  { value: 'failed', label: 'Failed' },
];

const KIND_FILTERS = [
  { value: 'all', label: 'All report types' },
  { value: 'remediation_report', label: 'Full remediation reports' },
  { value: 'category_report', label: 'Category PDFs' },
];

const _formatKind = (kind) => {
  if (!kind) return '—';
  if (KIND_LABELS[kind]) return KIND_LABELS[kind];
  return kind
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const _formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function RecentAIJobsPage() {
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);
  // Tracks whether the LAST poll succeeded — used to silence the toast
  // when /api/ai-jobs is temporarily 5xx so we don't spam an error
  // toast every 15 s while waiting for the backend to recover. We
  // fire the toast on the FIRST failure and the FIRST recovery only.
  const lastPollSucceededRef = useRef(true);

  const fetchJobs = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    const params = new URLSearchParams({ limit: '100' });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (kindFilter !== 'all') params.set('kind', kindFilter);
    try {
      const resp = await apiFetch(`${API_URL}/api/ai-jobs?${params.toString()}`);
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`HTTP ${resp.status}: ${body.slice(0, 200)}`);
      }
      const json = await resp.json();
      setJobs(json.jobs || []);
      // Recovery edge — surface a brief success toast so the user
      // knows the connection is healthy again. Suppressed on the
      // very first successful load so the page doesn't pop a toast
      // on every mount.
      if (lastPollSucceededRef.current === false) {
        toast.success('Reconnected — jobs list is up to date');
      }
      lastPollSucceededRef.current = true;
    } catch (err) {
      // Only toast on the FIRST consecutive failure. Subsequent
      // polls just keep retrying silently. The user can still see
      // the stale jobs already in state.
      if (lastPollSucceededRef.current) {
        toast.error('Could not load recent AI jobs', {
          description: err.message || 'Unknown error',
        });
      }
      lastPollSucceededRef.current = false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, kindFilter]);

  // Initial + on-filter-change fetch. A 15 s background poll keeps the
  // "Generating" rows live so the user sees them flip to "Ready" without
  // a manual refresh.
  useEffect(() => {
    fetchJobs(false);
    const interval = setInterval(() => fetchJobs(false), 15000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  /**
   * Download a completed job. Uses the same 3-tier flow as ResultsPage
   * (showSaveFilePicker → legacy anchor → iframe-safe manual toast)
   * so the experience is consistent everywhere a PDF is downloaded.
   */
  const downloadJob = async (job) => {
    setDownloadingId(job.id);
    const displayFilename = presentedAIJobFilename(job);
    const tLoad = toast.loading(`Preparing ${displayFilename || 'report'}…`);
    try {
      const resp = await apiFetch(`${API_URL}/api/ai-jobs/${job.id}/download`, {
        headers: { Accept: 'application/octet-stream' },
      });
      if (!resp.ok) {
        if (resp.status === 410) {
          throw new Error(
            'This report has been cleaned up by the 7-day retention sweeper. ' +
            'Re-open the audit to generate a fresh PDF.',
          );
        }
        const body = await resp.text().catch(() => '');
        throw new Error(`HTTP ${resp.status}: ${body.slice(0, 200)}`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : displayFilename;
      const outcome = await saveBlobWithPicker(blob, filename, job.mime_type || 'application/pdf');
      toast.dismiss(tLoad);
      if (outcome.cancelled) return;
      if (outcome.manual_action) {
        // Iframe-safe save: a toast button opens our helper page in a
        // new top-level tab, postMessages the blob to it, and the
        // helper page calls showSaveFilePicker from its top-level
        // context — yielding a real native Save As dialog. Same
        // pattern ResultsPage uses, so the experience is identical
        // whether the user re-downloads from here or from the
        // originating audit.
        toast.message(`${filename} is ready`, {
          description: `${_formatSize(blob.size)} · Click "Save report" to pick where to save it on your computer.`,
          duration: 30000,
          action: {
            label: 'Save report',
            onClick: () => {
              const opened = openManualDownload(outcome, (progress) => {
                if (progress.type === 'aha-save-complete') {
                  toast.success(`Saved · ${progress.filename}`, { duration: 5000 });
                } else if (progress.type === 'aha-save-cancelled') {
                  toast.message('Save cancelled', { duration: 4000 });
                } else if (progress.type === 'aha-save-error') {
                  toast.error('Could not save the file', {
                    description: progress.error || 'Unknown error',
                    duration: 10000,
                  });
                } else if (progress.type === 'popup-blocked') {
                  toast.error('Pop-up was blocked', {
                    description:
                      'Allow pop-ups for this site so AHA can open the Save dialog. The file opened in a preview tab as a fallback — use Ctrl/Cmd+S there.',
                    duration: 12000,
                  });
                }
              });
              if (!opened) {
                toast.error('Could not open the Save dialog', {
                  description:
                    'Allow pop-ups for this site, or open AHA in its own browser tab for a one-click Save dialog.',
                  duration: 12000,
                });
              }
            },
          },
        });
        return;
      }
      toast.success(
        outcome.location === 'picked'
          ? `Saved · ${outcome.filename}`
          : `Saved to Downloads · ${outcome.filename}`,
        { description: _formatSize(blob.size), duration: 6000 },
      );
      // The file was previously fetched within the 7-day TTL window —
      // refresh the row metadata so any newly-swept job shows the right
      // "Expired" state next time.
      fetchJobs(false);
    } catch (e) {
      toast.dismiss(tLoad);
      toast.error('Download failed', {
        description: e.message || 'Unknown error',
        duration: 10000,
      });
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="recent-ai-jobs-page">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2"
              data-testid="ai-jobs-title"
            >
              <Sparkles className="h-7 w-7 text-accent" />
              Recent AI Jobs
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              Every AI-generated report you've triggered. Cached on the
              server for 7 days — re-download without re-spending GPT-4o
              tokens. After that the file is swept and you can re-generate
              it from the originating audit.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => fetchJobs(true)}
            disabled={refreshing}
            data-testid="ai-jobs-refresh-btn"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-[200px]"
              data-testid="ai-jobs-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger
              className="w-[240px]"
              data-testid="ai-jobs-kind-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KIND_FILTERS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {jobs.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2" data-testid="ai-jobs-empty-title">
                No AI jobs yet
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Open a completed audit and click "Generate remediation
                report" or "Category PDF" — your jobs will appear here.
              </p>
              <Button
                onClick={() => navigate('/history')}
                data-testid="ai-jobs-go-history-btn"
              >
                Browse audit history
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="ai-jobs-list">
            {jobs.map((job) => {
              const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
              const StatusIcon = cfg.icon;
              const isExpired = job.status === 'complete' && !job.file_available;
              const canDownload = job.status === 'complete' && job.file_available;
              return (
                <Card
                  key={job.id}
                  className="hover:border-accent/50 transition-colors"
                  data-testid={`ai-job-card-${job.id}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-2.5 rounded-lg ${cfg.bg}`}>
                        <StatusIcon
                          className={`h-5 w-5 ${cfg.color} ${cfg.animate ? 'animate-spin' : ''}`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-foreground truncate">
                            {presentedAIJobFilename(job) || _formatKind(job.kind)}
                          </h3>
                          <Badge
                            variant="outline"
                            className="text-xs shrink-0"
                            data-testid={`ai-job-kind-${job.id}`}
                          >
                            {_formatKind(job.kind)}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`text-xs shrink-0 ${cfg.color}`}
                            data-testid={`ai-job-status-${job.id}`}
                          >
                            {cfg.label}
                          </Badge>
                          {isExpired && (
                            <Badge
                              variant="outline"
                              className="text-xs shrink-0 text-muted-foreground"
                              data-testid={`ai-job-expired-${job.id}`}
                            >
                              Expired
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                          <span>{formatDateTime(job.created_at)}</span>
                          {job.file_size > 0 && (
                            <span data-testid={`ai-job-size-${job.id}`}>
                              · {_formatSize(job.file_size)}
                            </span>
                          )}
                          {job.audit_id && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-accent hover:underline"
                              onClick={() => navigate(`/results/${job.audit_id}`)}
                              data-testid={`ai-job-audit-link-${job.id}`}
                            >
                              <ExternalLink className="h-3 w-3" />
                              Open audit
                            </button>
                          )}
                        </div>
                        {job.status === 'failed' && job.error && (
                          <div
                            className="mt-2 p-2 bg-[hsl(var(--destructive))]/5 rounded text-xs text-[hsl(var(--destructive))] flex items-start gap-2"
                            data-testid={`ai-job-error-${job.id}`}
                          >
                            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span className="break-words">{job.error}</span>
                          </div>
                        )}
                      </div>

                      <div className="shrink-0">
                        {canDownload && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadJob(job)}
                            disabled={downloadingId === job.id}
                            data-testid={`ai-job-download-${job.id}`}
                          >
                            {downloadingId === job.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            Download
                          </Button>
                        )}
                        {isExpired && job.audit_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/results/${job.audit_id}`)}
                            data-testid={`ai-job-regenerate-${job.id}`}
                          >
                            Re-generate
                          </Button>
                        )}
                        {(job.status === 'pending' || job.status === 'running') && (
                          <span
                            className="text-xs text-muted-foreground"
                            data-testid={`ai-job-progress-${job.id}`}
                          >
                            Refreshes every 15s
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
